import { useEffect, useRef, useState } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";

const MIN_VISIBLE_MS = 250;
const LINGER_MS      = 140;

export default function LoadingBar() {
  // Skip background polls (e.g. the 2s webhook-inbox refetch) — they'd
  // otherwise blink the top bar nonstop while idle. Queries opt in via
  // `meta: { background: true }`.
  const fetching = useIsFetching({
    predicate: (q) => !(q.meta as { background?: boolean } | undefined)?.background,
  });
  const mutating = useIsMutating();
  const pending = fetching + mutating;
  const [active, setActive] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (pending > 0) {
      if (!active) {
        setActive(true);
        shownAt.current = Date.now();
      }
      return;
    }
    const sinceShown = shownAt.current ? Date.now() - shownAt.current : MIN_VISIBLE_MS;
    const wait = Math.max(LINGER_MS, MIN_VISIBLE_MS - sinceShown);
    const t = window.setTimeout(() => {
      setActive(false);
      shownAt.current = null;
    }, wait);
    return () => window.clearTimeout(t);
  }, [pending, active]);

  return (
    <div className="loading-bar" aria-hidden={!active} data-active={active}>
      <span className="bar-1" />
      <span className="bar-2" />
    </div>
  );
}
